import type { PuckComponent } from "@/hooks/use-page-ai";

export type SectionReviewChoice = "approved" | "change" | "redo";

const TITLE_FIELDS = ["heading", "title", "label", "name"] as const;
const SUMMARY_FIELDS = ["subheading", "body", "description", "buttonText"] as const;

function readStringField(
  component: PuckComponent,
  fieldNames: readonly string[],
): string | null {
  const props = component.props ?? {};

  for (const fieldName of fieldNames) {
    const value = props[fieldName];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

function prettifyComponentType(type: string) {
  return type
    .split(/[-_]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

export function getSectionReviewKey(
  versionNumber: number,
  component: PuckComponent,
  index: number,
) {
  const props = component.props ?? {};
  const componentId = typeof props.id === "string" && props.id.trim().length > 0
    ? props.id.trim()
    : `${component.type}-${index + 1}`;

  return `v${versionNumber}:${componentId}:${index}`;
}

export function getSectionReviewTitle(component: PuckComponent, index: number) {
  return readStringField(component, TITLE_FIELDS) ?? `${prettifyComponentType(component.type)} ${index + 1}`;
}

export function getSectionReviewSummary(component: PuckComponent) {
  const summary = readStringField(component, SUMMARY_FIELDS);
  if (summary) {
    return truncate(summary, 140);
  }

  const configuredFieldCount = Object.entries(component.props ?? {}).filter(([, value]) => {
    return typeof value === "string" && value.trim().length > 0;
  }).length;

  if (configuredFieldCount > 0) {
    return `${configuredFieldCount} content details are ready for review.`;
  }

  return "Review the layout and wording for this section.";
}

export function buildSectionReviewInstruction(input: {
  component: PuckComponent;
  index: number;
  mode: Exclude<SectionReviewChoice, "approved">;
  feedback?: string;
}) {
  const sectionLabel = `section ${input.index + 1} (${getSectionReviewTitle(input.component, input.index)})`;

  if (input.mode === "redo") {
    return `Create a new draft version and completely rethink ${sectionLabel}. Keep the rest of the page as stable as possible unless a small supporting edit is necessary. Replace this section with a fresh approach that still matches the offer and goal of the page.`;
  }

  const feedback = input.feedback?.trim();
  if (!feedback) {
    throw new Error("Section feedback is required before requesting changes.");
  }

  return `Create a new draft version and revise ${sectionLabel}. Keep the rest of the page as stable as possible unless a small supporting edit is necessary. Make these changes: ${feedback}`;
}

export function getSectionApprovalSummary(
  reviewKeys: string[],
  decisions: Record<string, SectionReviewChoice>,
) {
  const approvedCount = reviewKeys.filter((key) => decisions[key] === "approved").length;

  return {
    approvedCount,
    totalCount: reviewKeys.length,
    allApproved: reviewKeys.length > 0 && approvedCount === reviewKeys.length,
  };
}
